import React, { useMemo, useRef } from 'react';
import styled from 'styled-components';
import { TextDefault } from '@awarego-ui/components';
import { highlightCss, lintPlayerCss } from '../../utils/playerCss';

const Wrap = styled.div`
    display: grid;
    width: 100%;
    border: 1px solid ${(p) => (p.$error ? '#d32f2f' : '#e0e0e0')};
    border-radius: 4px;
    background: #f7f8f7;
    &:focus-within {
        border-color: ${(p) => (p.$error ? '#d32f2f' : '#20a26b')};
        box-shadow: 0 0 0 1px
            ${(p) => (p.$error ? 'rgba(211,47,47,0.25)' : 'rgba(32,162,107,0.25)')};
    }
`;

const Highlight = styled.pre`
    grid-area: 1 / 1;
    margin: 0;
    padding: 12px;
    min-height: 280px;
    height: 100%;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 12px;
    line-height: 1.55;
    white-space: pre;
    word-wrap: normal;
    overflow: auto;
    tab-size: 2;
    box-sizing: border-box;
    pointer-events: none;
    color: #24292e;
    .pce-comment {
        color: #6a737d;
        font-style: italic;
    }
    .pce-string {
        color: #032f62;
    }
    .pce-at {
        color: #e36209;
        font-weight: 600;
    }
    .pce-at-bad {
        color: #d73a49;
        text-decoration: wavy underline;
    }
    .pce-selector {
        color: #22863a;
    }
    .pce-property {
        color: #005cc5;
    }
    .pce-variable {
        color: #6f42c1;
        font-weight: 600;
    }
    .pce-property-bad {
        color: #d73a49;
        text-decoration: wavy underline;
    }
    .pce-value {
        color: #24292e;
    }
    .pce-punct {
        color: #6a737d;
    }
`;

const Area = styled.textarea`
    grid-area: 1 / 1;
    margin: 0;
    padding: 12px;
    min-height: 280px;
    height: 100%;
    width: 100%;
    resize: vertical;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 12px;
    line-height: 1.55;
    white-space: pre;
    word-wrap: normal;
    overflow: auto;
    tab-size: 2;
    box-sizing: border-box;
    border: 0;
    outline: none;
    background: transparent;
    caret-color: #111;
    color: ${(p) => (p.$empty ? '#24292e' : 'transparent')};
    z-index: 1;
    &::placeholder {
        color: #9aa0a6;
    }
`;

const Issues = styled.ul`
    list-style: none;
    margin: 8px 0 0;
    padding: 0;
    font-size: 12px;
    line-height: 1.45;
`;

const Issue = styled.li`
    color: ${(p) => (p.$error ? '#c62828' : '#b26a00')};
    margin-bottom: 2px;
`;

function PlayerCssEditor({ value, onChange, placeholder }) {
    const preRef = useRef(null);
    const areaRef = useRef(null);
    const css = value || '';
    const empty = !css;
    const html = useMemo(() => highlightCss(css) + '\n', [css]);
    const issues = useMemo(() => lintPlayerCss(css), [css]);
    const hasError = issues.some((i) => i.severity === 'error');

    const syncScroll = () => {
        if (!preRef.current || !areaRef.current) return;
        preRef.current.scrollTop = areaRef.current.scrollTop;
        preRef.current.scrollLeft = areaRef.current.scrollLeft;
    };

    const onKeyDown = (e) => {
        if (e.key !== 'Tab') return;
        e.preventDefault();
        const el = e.target;
        const start = el.selectionStart;
        const end = el.selectionEnd;
        const next = css.slice(0, start) + '  ' + css.slice(end);
        onChange(next);
        requestAnimationFrame(() => {
            el.selectionStart = el.selectionEnd = start + 2;
        });
    };

    return (
        <div>
            <Wrap $error={hasError}>
                <Highlight
                    ref={preRef}
                    aria-hidden="true"
                    dangerouslySetInnerHTML={{ __html: empty ? '' : html }}
                />
                <Area
                    ref={areaRef}
                    $empty={empty}
                    value={css}
                    spellCheck={false}
                    autoCapitalize="off"
                    autoCorrect="off"
                    autoComplete="off"
                    placeholder={placeholder}
                    onChange={(e) => onChange(e.target.value)}
                    onScroll={syncScroll}
                    onKeyDown={onKeyDown}
                    aria-label="Custom CSS"
                />
            </Wrap>
            {issues.length ? (
                <Issues>
                    {issues.slice(0, 8).map((issue, idx) => (
                        <Issue key={idx} $error={issue.severity === 'error'}>
                            {issue.line ? `Line ${issue.line}: ` : ''}
                            {issue.message}
                        </Issue>
                    ))}
                    {issues.length > 8 ? (
                        <Issue>
                            {issues.length - 8} more issue
                            {issues.length - 8 === 1 ? '' : 's'}
                        </Issue>
                    ) : null}
                </Issues>
            ) : css.trim() ? (
                <TextDefault
                    lighter
                    style={{ fontSize: 12, marginTop: 8, opacity: 0.8 }}
                >
                    CSS looks valid. Unknown properties are underlined and will
                    be dropped on save.
                </TextDefault>
            ) : null}
        </div>
    );
}

export default PlayerCssEditor;
